import { useEffect, useRef, useState } from "react";

export interface Bar {
  label: string; // short axis label, e.g. "12 Oct"
  full: string; // tooltip heading, e.g. "Week of 12 Oct"
  value: number;
}

/**
 * BarChart draws one series as thin bars with a hover tooltip and an
 * accessible table view. One series only: never a second axis.
 */
export function BarChart({ title, bars, format, height = 170 }: { title: string; bars: Bar[]; format: (v: number) => string; height?: number }) {
  const [hover, setHover] = useState<number | null>(null);
  const [table, setTable] = useState(false);
  // Draw at the real width so text stays readable (no scaling).
  const box = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(560);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setW(Math.max(240, Math.round(e.contentRect.width))));
    ro.observe(el);
    return () => {
      ro.disconnect();
    };
  }, [table]);
  const max = Math.max(...bars.map((b) => b.value), 0);
  const top = niceMax(max);
  const pad = { l: 40, r: 6, t: 10, b: 22 };
  const plotW = w - pad.l - pad.r;
  const plotH = height - pad.t - pad.b;
  const slot = plotW / Math.max(bars.length, 1);
  const barW = Math.max(2, Math.min(28, slot - 2)); // 2px surface gap between bars
  const y = (v: number) => pad.t + plotH - (top ? (v / top) * plotH : 0);
  const every = Math.ceil(bars.length / Math.max(2, Math.floor(plotW / 64))); // keep axis labels from colliding
  const total = bars.reduce((s, b) => s + b.value, 0);
  const h = hover != null ? bars[hover] : null;

  return (
    <figure className="chart">
      <figcaption>
        <b>{title}</b>
        <span className="muted small">{h ? `${h.full}: ${format(h.value)}` : `Total ${format(total)}`}</span>
        <button type="button" className="link small" onClick={() => setTable(!table)}>{table ? "Show chart" : "Show table"}</button>
      </figcaption>
      {table ? (
        <table className="table small">
          <thead><tr><th>Period</th><th className="num">{title}</th></tr></thead>
          <tbody>{bars.map((b, i) => <tr key={i}><td>{b.full}</td><td className="num">{format(b.value)}</td></tr>)}</tbody>
        </table>
      ) : (
        <div ref={box}>
        <svg width={w} height={height} viewBox={`0 0 ${w} ${height}`} role="img" aria-label={`${title}: total ${format(total)}`} onMouseLeave={() => setHover(null)}>
          {[0, 0.5, 1].map((f) => (
            <g key={f}>
              <line x1={pad.l} x2={w - pad.r} y1={y(top * f)} y2={y(top * f)} className="chart-grid" />
              <text x={pad.l - 6} y={y(top * f) + 4} textAnchor="end" className="chart-tick">{compact(top * f)}</text>
            </g>
          ))}
          {bars.map((b, i) => {
            const x = pad.l + i * slot + (slot - barW) / 2;
            const bh = Math.max(0, pad.t + plotH - y(b.value));
            const r = Math.min(4, barW / 2, bh);
            return (
              <g key={i}>
                {bh > 0 && (
                  <path className={`chart-bar${hover === i ? " active" : ""}`}
                    d={`M${x},${pad.t + plotH} V${y(b.value) + r} Q${x},${y(b.value)} ${x + r},${y(b.value)} H${x + barW - r} Q${x + barW},${y(b.value)} ${x + barW},${y(b.value) + r} V${pad.t + plotH} Z`} />
                )}
                {/* Hit target bigger than the mark */}
                <rect x={pad.l + i * slot} y={pad.t} width={slot} height={plotH} fill="transparent" onMouseEnter={() => setHover(i)} />
                {i % every === 0 && <text x={x + barW / 2} y={height - 6} textAnchor="middle" className="chart-tick">{b.label}</text>}
              </g>
            );
          })}
          <line x1={pad.l} x2={w - pad.r} y1={pad.t + plotH} y2={pad.t + plotH} className="chart-axis" />
        </svg>
        </div>
      )}
    </figure>
  );
}

// niceMax rounds up to 1, 2 or 5 × 10^n so gridlines land on round numbers.
function niceMax(v: number): number {
  if (v <= 0) return 1;
  const p = 10 ** Math.floor(Math.log10(v));
  for (const m of [1, 2, 5, 10]) if (v <= m * p) return m * p;
  return 10 * p;
}

// compact writes axis numbers short: 1.5k, 2M.
function compact(v: number): string {
  if (v >= 1e6) return `${+(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${+(v / 1e3).toFixed(1)}k`;
  return `${+v.toFixed(v < 10 ? 1 : 0)}`;
}
